import React from 'react';
import { NavLink, useHistory } from 'react-router-dom';
import useCachedQuery from '../../shared/hooks/useCachedQuery';
import { OverlayTrigger } from '@econ/ui-elements';
import AgreementDetails from './AgreementDetails';
import Quicksearch from './Quicksearch';
import icons from './icons';
import { Logo } from '../Logo/Logo';
import Navigation, { Link } from './Navigation';
import SettingsPopover from './SettingsPopover';
import Authorize from '../../shared/components/Authorize';
import { text } from '../../shared/components/l10n/TextByRoute';
import { EContext } from '../../shared/components/EContext';
import Intercom from '../../shared/components/Intercom';
import { CustomTab } from '../../../types';
import { useIntroductionTour } from './IntroductionTour/useIntroductionTour';
import IntroductionTour from './IntroductionTour/IntroductionTour';

import './style.css';
import { Hanger } from '@economic/gaudi';
import { tBB } from '../../shared/components/l10n/l10n';
import { useInboxHanger } from './useInboxHanger';

export default function Header() {
    const { identity, routes, texts } = React.useContext(EContext);
    const agreementsQuery = useCachedQuery<CustomTab[]>('/agreements', []);
    const customTabsQuery = useCachedQuery<CustomTab[]>('/user/tabs/custom', []);
    const homeRoute = routes['/home'] ? '/home' : '/';
    const [showTour, setShowTour] = useIntroductionTour();
    const history = useHistory();

    function hideTourForeverAndNavigateToMasterInformation(doneAllSteps) {
        setShowTour(false);

        if (doneAllSteps) {
            history.push('/settings/company/master-information');
        }
    }

    const [showHanger, hideHanger] = useInboxHanger();

    return (
        <>
            {showTour && <IntroductionTour onDone={hideTourForeverAndNavigateToMasterInformation} />}
            <header id="header">
                <NavLink id="logo" to={homeRoute}>
                    <Logo white />
                </NavLink>

                <Navigation id="primary_navigation" autoCollapse>
                    <Link route="/system" icon="system">
                        System
                    </Link>
                    <Link route="/home" icon="home" name="home">
                        Home
                    </Link>
                    <Link route="/basic" icon="basic">
                        Basic
                    </Link>
                    <Link route="/developer" icon="developer">
                        Developer
                    </Link>
                    <Link route="/partner-api" icon="partnerApi">
                        Partner api
                    </Link>
                    <Link route="/sales" icon="sales" name="sales">
                        Sales
                    </Link>
                    <Link route="/accounting" icon="accounting" name="accounting">
                        Accounting
                    </Link>
                    <Link route="/reports" icon="reports" name="reports">
                        Reports
                    </Link>
                    <Link route="/inventory" icon="inventory">
                        Inventory
                    </Link>
                    <Link route="/projects" icon="projects">
                        Projects
                    </Link>
                    <Link route="/website" icon="website">
                        Website
                    </Link>
                    <Link route="/webshop" icon="webshop">
                        Webshop
                    </Link>
                    <Link route="/webshop-plus" icon="webshop">
                        Webshop
                    </Link>
                    <Link route="/journal-pro" icon="journalPro">
                        Journal Pro
                    </Link>
                    {customTabsQuery.data.map((customTab, index) => (
                        <Link key={index} route={`/custom?uri=${customTab.url}`}>
                            {customTab.name}
                        </Link>
                    ))}
                </Navigation>

                <Authorize route="/search">
                    <Quicksearch />
                </Authorize>

                <Navigation id="secondary_navigation" style={{ position: 'relative' }}>
                    <Link route="/idea-bank" icon="ideaBank" tooltip>
                        Idea bank
                    </Link>

                    {showHanger ? (
                        <Hanger
                            disclosure={
                                <Link route="/inbox" icon="inbox" tooltip>
                                    Inbox
                                </Link>
                            }
                            placement="bottom"
                            aria-label="Hanger"
                            onClosed={() => hideHanger()}>
                            {tBB(`HANGER_INBOX`)}
                        </Hanger>
                    ) : (
                        <Link route="/inbox" icon="inbox" tooltip>
                            Inbox
                        </Link>
                    )}

                    <Link route="/market" icon="market" tooltip name="market">
                        Market
                    </Link>

                    <Authorize route="/chat">
                        <li>
                            <Intercom>
                                {({ toggle }) => (
                                    <button
                                        className="button__green"
                                        onClick={() => toggle()}
                                        aria-label={text(texts)('Chat', '/chat')}
                                        data-microtip-position="bottom"
                                        role="tooltip"
                                        name="chat">
                                        {icons.chat}
                                    </button>
                                )}
                            </Intercom>
                        </li>
                    </Authorize>
                    <Link
                        route="/external/e-copedia"
                        href="https://www.e-conomic.dk/support/e-copedia"
                        icon="ecopedia"
                        className="button__green"
                        tooltip
                        name="ecopedia">
                        e-copedia
                    </Link>
                    <li>
                        <OverlayTrigger overlay={SettingsPopover()} position="bottom">
                            {({ toggleOverlay }) => (
                                <button
                                    className="button__orange"
                                    onClick={() => toggleOverlay()}
                                    aria-label={text(texts)('Settings', '/settings')}
                                    data-microtip-position="bottom"
                                    role="tooltip"
                                    name="settings">
                                    {icons.settings}
                                </button>
                            )}
                        </OverlayTrigger>
                    </li>
                </Navigation>

                <AgreementDetails
                    currentAgreement={identity!.currentAgreement}
                    otherAgreements={agreementsQuery.data}
                    refetchAgreements={agreementsQuery.refetch}
                    user={identity!.user}
                    session={identity!.session}
                />
            </header>
        </>
    );
}
